import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import BrandSelect from '../../Common/BrandSelect';
import Callout from '../../Common/Callout';
import { Section } from '../Personell/EmployeeDetailParts';
import './semester.css';
import { Save, RefreshCw, Zap } from 'lucide-react';
import Swal from 'sweetalert2';
import * as payrollApi from '../../../../services/api/payrollApi';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';

/**
 * VacationBalanceSection — payroll-admin form for setting each employee's
 * semester balance (Semesterlagen Layer 2). Rendered as a section inside
 * the unified `SemesterPage`.
 *
 * Backs:
 *   GET  /payroll/vacation-balance?employeeId=...
 *   PUT  /payroll/vacation-balance
 *
 * Layer 2 model: earned/carried/saved are admin-set (no auto-accrual),
 * used is derived from approved SEMESTER time_entry rows. This section
 * only edits the three admin-set fields.
 */
const VacationBalanceSection = () => {
  const { companyId } = useParams();

  const currentYear = (() => {
    const now = new Date();
    // Semesterår April-March default. Year of April 1 start.
    return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  })();

  const [year, setYear] = useState(currentYear);
  const [employees, setEmployees] = useState([]);
  const [snapshots, setSnapshots] = useState({}); // employeeId → snapshot
  const [drafts, setDrafts] = useState({});       // employeeId → { earnedDays, carriedOverDays, savedDays }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});        // employeeId → bool
  const [accruing, setAccruing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch active employees via the existing hr/employees endpoint.
      // No dedicated personellApi module exists — the pattern in
      // EmployeeList calls this URL directly.
      const empRes = await axios.get(getApiUrl(`${companyId}/hr/employees`), { withCredentials: true });
      const emps = empRes.data?.activeEmployees || empRes.data?.employees || [];
      setEmployees(emps);

      // Fetch snapshots for each employee in parallel
      const snapEntries = await Promise.all(
        emps.map(async (e) => {
          try {
            const r = await payrollApi.getVacationBalance(companyId, { employeeId: e.employee_id });
            return [e.employee_id, r.data?.snapshot || null];
          } catch {
            return [e.employee_id, null];
          }
        }),
      );
      const snapMap = Object.fromEntries(snapEntries);
      setSnapshots(snapMap);

      // Seed drafts from current snapshot values so typing replaces
      // them cleanly.
      const draftMap = {};
      for (const [empId, snap] of snapEntries) {
        if (snap) {
          draftMap[empId] = {
            earnedDays: String(snap.earnedDays || 0),
            carriedOverDays: String(snap.carriedOverDays || 0),
            savedDays: String(snap.savedDays || 0),
          };
        }
      }
      setDrafts(draftMap);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte ladda semesterdata');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  const setDraftField = (empId, field, value) => {
    setDrafts(prev => ({
      ...prev,
      [empId]: { ...(prev[empId] || {}), [field]: value },
    }));
  };

  const handleAccrueAll = async () => {
    const confirm = await Swal.fire({
      title: `Kör §7-intjäning för semesterår ${year}/${year + 1}?`,
      html: (
        'Beräknar tjänade dagar (prorata efter anställningstid under intjänandeåret). '
        + 'Uppdaterar alla aktiva anställda. Manuellt satta värden skrivs <strong>inte</strong> över '
        + 'om force-flaggan är avstängd.'
      ),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Kör intjäning',
      cancelButtonText: 'Avbryt',
    });
    if (!confirm.isConfirmed) return;

    setAccruing(true);
    setError(null);
    try {
      const res = await payrollApi.accrueVacationBalance(companyId, { year, force: false });
      const data = res.data?.data || res.data;
      const count = data?.updated?.length ?? data?.count ?? 0;
      setSuccess(`Intjäning klar. ${count} anställda uppdaterade.`);
      setTimeout(() => setSuccess(null), 5000);
      await loadData();
    } catch (err) {
      // §18 cap violation comes back as 409 / 422 with a structured message.
      const msg = err.response?.data?.message || err.response?.data?.error || err.message;
      await Swal.fire({
        title: 'Intjäning blockerad',
        text: msg || 'Något gick fel vid intjäning.',
        icon: 'error',
      });
    } finally {
      setAccruing(false);
    }
  };

  const handleSave = async (empId) => {
    setSaving(prev => ({ ...prev, [empId]: true }));
    setError(null);
    try {
      const d = drafts[empId] || {};
      const payload = {
        employeeId: empId,
        year,
        earnedDays: Number(d.earnedDays) || 0,
        carriedOverDays: Number(d.carriedOverDays) || 0,
        savedDays: Number(d.savedDays) || 0,
      };
      await payrollApi.setVacationBalance(companyId, payload);
      // Refresh just this employee's snapshot
      const r = await payrollApi.getVacationBalance(companyId, { employeeId: empId });
      setSnapshots(prev => ({ ...prev, [empId]: r.data?.snapshot || null }));
      setSuccess('Semestersaldo sparat');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Kunde inte spara semestersaldo');
    } finally {
      setSaving(prev => ({ ...prev, [empId]: false }));
    }
  };

  const yearOptions = Array.from({ length: 7 }, (_, i) => currentYear - 3 + i)
    .map(y => ({ value: y, label: `Semesterår ${y}/${y + 1} (1 apr ${y} – 31 mar ${y + 1})` }));

  return (
    <Section
      id="saldon-heading"
      title="Semestersaldon"
      hint="Tjänat, överförda och sparat sätts här av lönehandläggaren. Använd hämtas från godkända semesterposter på lönespecarna. Kvar = tjänat + överförda − använd, lägst noll. Förskott uppstår när använd överstiger tjänat + överförda; det tillåts men visas."
      meta={`Semesterår ${year}/${year + 1}, 1 april ${year} – 31 mars ${year + 1}.`}
    >
      {error && <Callout tone="danger" role="alert">{error}</Callout>}
      {success && <Callout tone="success">{success}</Callout>}

      <div className="sem__toolbar">
        <div className="sem__toolbar-year">
          <BrandSelect
            name="year"
            value={year}
            options={yearOptions}
            onChange={(e) => setYear(Number(e.target.value))}
          />
        </div>
        <div className="sem__toolbar-actions">
          <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" onClick={loadData} disabled={loading}>
            <RefreshCw />
            Uppdatera
          </button>
          <button
            type="button"
            className="ll-btn ll-btn--primary ll-btn--sm"
            onClick={handleAccrueAll}
            disabled={accruing || loading}
            title="Kör intjäning enligt semesterlagen 7 § för alla aktiva anställda"
          >
            {accruing ? <Spinner animation="border" size="sm" /> : <Zap />}
            {accruing ? 'Intjänar…' : 'Intjäna 7 § för alla'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-4 text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          Hämtar semestersaldon…
        </div>
      ) : (
        <div className="ll-table-card table-responsive">
          <table className="ll-table">
            <thead>
              <tr>
                <th>Anställd</th>
                <th className="ll-num">Tjänat</th>
                <th className="ll-num">Överförda</th>
                <th className="ll-num">Sparat</th>
                <th className="ll-num">Använd</th>
                <th className="ll-num">Kvar</th>
                <th className="ll-num">Förskott</th>
                <th className="ll-table__actions" />
              </tr>
            </thead>
            <tbody>
              {employees.map(emp => {
                const empId = emp.employee_id;
                const snap = snapshots[empId] || {};
                const draft = drafts[empId] || { earnedDays: '0', carriedOverDays: '0', savedDays: '0' };
                const dirty = {
                  earnedDays: Number(draft.earnedDays) !== Number(snap.earnedDays || 0),
                  carriedOverDays: Number(draft.carriedOverDays) !== Number(snap.carriedOverDays || 0),
                  savedDays: Number(draft.savedDays) !== Number(snap.savedDays || 0),
                };
                const isDirty = dirty.earnedDays || dirty.carriedOverDays || dirty.savedDays;
                const hasAdvance = (snap.advanceDaysTaken || 0) > 0;
                const field = (name, label) => (
                  <td className="ll-num">
                    <input
                      type="number" min="0" step="0.5"
                      className={`sem__input${dirty[name] ? ' is-dirty' : ''}`}
                      value={draft[name]}
                      onChange={e => setDraftField(empId, name, e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && isDirty) handleSave(empId); }}
                      aria-label={`${label} för ${emp.firstname} ${emp.lastname}`}
                    />
                  </td>
                );
                return (
                  <tr key={empId}>
                    <td style={{ fontWeight: 500 }}>{emp.firstname} {emp.lastname}</td>
                    {field('earnedDays', 'Tjänade semesterdagar')}
                    {field('carriedOverDays', 'Överförda semesterdagar')}
                    {field('savedDays', 'Sparade semesterdagar')}
                    <td className="ll-num" style={{ color: '#6b7280' }}>{snap.usedDays ?? '–'}</td>
                    <td className="ll-num" style={{ fontWeight: 600, color: (snap.remainingPaidDays || 0) > 0 ? '#111827' : '#9ca3af' }}>
                      {snap.remainingPaidDays ?? '–'}
                    </td>
                    <td className="ll-num" style={{ color: hasAdvance ? '#9a4b00' : '#9ca3af', fontWeight: hasAdvance ? 600 : 400 }}>
                      {snap.advanceDaysTaken ?? '–'}
                    </td>
                    <td className="ll-table__actions">
                      <button
                        type="button"
                        className="ll-btn ll-btn--primary ll-btn--sm"
                        onClick={() => handleSave(empId)}
                        disabled={!isDirty || saving[empId]}
                        style={{ visibility: isDirty ? 'visible' : 'hidden' }}
                      >
                        {saving[empId] ? <Spinner animation="border" size="sm" /> : <Save />}
                        Spara
                      </button>
                    </td>
                  </tr>
                );
              })}
              {employees.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">Inga aktiva anställda.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
};

export default VacationBalanceSection;
